import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from './Button';

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const LIMIT_OPTIONS = [10, 30, 50, 100, 1000];

export function Pagination({
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  onLimitChange,
}: PaginationProps) {
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
      {/* 表示件数 */}
      <div className="flex items-center gap-3">
        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          表示件数:
        </span>
        <select
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          className="px-2 py-1 border rounded text-sm"
          style={{
            backgroundColor: 'var(--background-card)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)',
          }}
        >
          {LIMIT_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}件
            </option>
          ))}
        </select>
        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          {total.toLocaleString()} 件中 {startItem.toLocaleString()} - {endItem.toLocaleString()} 件
        </span>
      </div>

      {/* ページネーション */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          disabled={page === 1}
          onClick={() => onPageChange(1)}
          title="最初のページ"
        >
          <ChevronsLeft size={16} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
          title="前のページ"
        >
          <ChevronLeft size={16} />
        </Button>
        
        {/* ページ番号 */}
        <div className="flex items-center gap-1 mx-2">
          {generatePageNumbers(page, totalPages).map((pageNum, idx) => (
            pageNum === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-2" style={{ color: 'var(--text-secondary)' }}>
                ...
              </span>
            ) : (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum as number)}
                className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                  page === pageNum
                    ? 'bg-blue-600 text-white'
                    : 'hover:bg-gray-100'
                }`}
                style={page !== pageNum ? { color: 'var(--text-primary)' } : {}}
              >
                {pageNum}
              </button>
            )
          ))}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          disabled={page === totalPages || totalPages === 0}
          onClick={() => onPageChange(page + 1)}
          title="次のページ"
        >
          <ChevronRight size={16} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={page === totalPages || totalPages === 0}
          onClick={() => onPageChange(totalPages)}
          title="最後のページ"
        >
          <ChevronsRight size={16} />
        </Button>
      </div>
    </div>
  );
}

function generatePageNumbers(current: number, total: number): (number | string)[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  
  if (current <= 3) {
    pages.push(1, 2, 3, 4, '...', total);
  } else if (current >= total - 2) {
    pages.push(1, '...', total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '...', current - 1, current, current + 1, '...', total);
  }

  return pages;
}
